import { createReducer } from 'store/createReducer';
import { ConservatoryDesignModel } from 'models/ConservatoryDesignModel';
import { ConservatoryStepValid } from 'models/ConservatoryStepValid';
import {
  GUIDE_DESIGN_DATA_LOADING,
  GUIDE_DESIGN_DATA_LOADED,
  GUIDE_DESIGN_PRODUCTS_BY_OPENING_SIZE,
  GUIDE_DESIGN_SELECT_PRODUCT,
  GUIDE_DESIGN_SELECT_TYPE,
  GUIDE_DESIGN_INVALID,
  GUIDE_DESIGN_VALID,
  GUIDE_DESIGN_CLOSE_OPENING_SELECTOR,
  ConservatoryDesignAction,
  ConservatoryDesignProductsByOpeningSizeAction,
  ConservatoryDesignSelectProductAction,
  ConservatoryDesignSelectTypeAction,
  GUIDE_DESIGN_SELECT_COLOR,
  ConservatoryDesignSelectColorAction,
  ConservatoryDesignCloseOpeningSelectorAction
} from './actions';
import {
  GUIDE_SIZE_TOGGLE_CORNERS,
  GUIDE_SIZE_SELECT_WIDTH,
  GUIDE_SIZE_SELECT_DEPTH
} from '../Size/actions';
import { GUIDE_SEASON_PRODUCT_SELECTED } from '../Season/actions';

const resetState = (state: ConservatoryDesignModel): ConservatoryDesignModel => {
  return {
    ...state,
    openings: []
  };
};

const designReducer = {
  [GUIDE_SEASON_PRODUCT_SELECTED]: (state: ConservatoryDesignModel) => {
    return resetState(state);
  },
  [GUIDE_SIZE_TOGGLE_CORNERS]: (state: ConservatoryDesignModel) => {
    return resetState(state);
  },
  [GUIDE_SIZE_SELECT_WIDTH]: (state: ConservatoryDesignModel) => {
    return resetState(state);
  },
  [GUIDE_SIZE_SELECT_DEPTH]: (state: ConservatoryDesignModel) => {
    return resetState(state);
  },
  [GUIDE_DESIGN_VALID]: (state: ConservatoryDesignModel) => {
    return {
      ...state,
      isValid: ConservatoryStepValid.Valid
    };
  },
  [GUIDE_DESIGN_INVALID]: (state: ConservatoryDesignModel) => {
    return {
      ...state,
      isValid: ConservatoryStepValid.Invalid
    };
  },
  [GUIDE_DESIGN_DATA_LOADED]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignAction
  ) => {
    return {
      ...state,
      openings: action.data.openings.map(o => {
        const defaultSelectedTypeName = o.id === 'TopWindow' ? 'Gavelspetsfönster' : 'Dörr';
        const defaultSelectedColor = 'vit';
        const defaultSelectedProduct = o.productsWithProps.filter(
          p => p.color.toLowerCase() === defaultSelectedColor.toLowerCase()
        );

        return {
          ...o,
          selectedTypeName: defaultSelectedTypeName,
          productsWithProps: o.productsWithProps.map(p => {
            return {
              ...p,

              selected:
                defaultSelectedProduct.length > 0
                  ? defaultSelectedProduct[0].productId === p.productId
                  : false
            };
          }),

          selectedProductId:
            defaultSelectedProduct.length > 0 ? defaultSelectedProduct[0].productId : ''
        };
      }),
      configuratorImage: action.data.configuratorImage,
      dataLoading: false
    };
  },
  [GUIDE_DESIGN_DATA_LOADING]: (state: ConservatoryDesignModel) => {
    return {
      ...state,
      dataLoading: true
    };
  },
  [GUIDE_DESIGN_PRODUCTS_BY_OPENING_SIZE]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignProductsByOpeningSizeAction
  ) => {
    return {
      ...state,
      isValid: ConservatoryStepValid.Unknown,
      openings: state.openings.map(opening => {
        return {
          ...opening,
          selected: opening.id === action.id
        };
      })
    };
  },
  [GUIDE_DESIGN_SELECT_PRODUCT]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignSelectProductAction
  ) => {
    return {
      ...state,
      isValid: ConservatoryStepValid.Unknown,
      openings: state.openings.map(opening => {
        if (opening.id === action.id) {
          const productsWithProps = opening.productsWithProps.map(p => {
            return {
              ...p,
              selected: p.productId === action.productId
            };
          });

          return {
            ...opening,
            productsWithProps
          };
        } else {
          return opening;
        }
      })
    };
  },
  [GUIDE_DESIGN_SELECT_TYPE]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignSelectTypeAction
  ) => {
    return {
      ...state,
      openings: state.openings.map(opening => {
        if (opening.selected) {
          const typeItems = opening.productsWithProps.filter(p => p.type === action.name);
          const autoSelectedItem = typeItems.length > 0 ? typeItems[0].productId : undefined;

          const productsWithProps = opening.productsWithProps.map(p => {
            return {
              ...p,
              selected: p.productId === autoSelectedItem ? true : false
            };
          });

          return {
            ...opening,
            productsWithProps,
            selectedTypeName: action.name,
            selectedProductId: autoSelectedItem
          };
        }

        return opening;
      })
    };
  },
  [GUIDE_DESIGN_SELECT_COLOR]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignSelectColorAction
  ) => {
    return {
      ...state,
      openings: state.openings.map(opening => {
        if (opening.selected) {
          return {
            ...opening,
            selectedProductId: action.productId
          };
        }
        return opening;
      })
    };
  },
  [GUIDE_DESIGN_CLOSE_OPENING_SELECTOR]: (
    state: ConservatoryDesignModel,
    action: ConservatoryDesignCloseOpeningSelectorAction
  ) => {
    let color = '';
    let selectedProductId = '';
    let selectedTypeName = '';

    for (const opening of state.openings) {
      if (opening.id === action.id) {
        for (const product of opening.productsWithProps) {
          if (product.selected) {
            color = product.color;
            selectedProductId = product.productId;
            selectedTypeName = product.type;
          }
        }
      }
    }

    return {
      ...state,
      isValid: ConservatoryStepValid.Unknown,
      openings: state.openings.map(o => {
        return {
          ...o,
          selected: false,
          selectedTypeName: action.useSameColor ? selectedTypeName : o.selectedTypeName,
          selectedProductId: action.useSameColor ? selectedProductId : o.selectedProductId,
          productsWithProps: o.productsWithProps.map(p => {
            if (action.useSameColor) {
              if (o.productsWithProps.some(p => p.color === color && p.type === selectedTypeName)) {
                return {
                  ...p,
                  selected: p.color === color && p.type === selectedTypeName
                };
              }

              return {
                ...p,
                selected: p.selected
              };
            }

            return { ...p };
          })
        };
      })
    };
  }
};

export default createReducer(
  {
    configuratorImage: '',
    dataLoading: false,
    isValid: ConservatoryStepValid.Unknown,
    openings: []
  },
  designReducer
);
